import { createReducer } from '@reduxjs/toolkit'

import {
  Field,
  PresetRange,
  resetMintState,
  setFullRange,
  setPresetRange,
  setPresetTicks,
  typeInput,
  typeLeftRangeInput,
  typeLeftRangeTick,
  typeRightRangeInput,
  typeRightRangeTick,
  typeStartPriceInput,
} from './actions'
export type FullRange = true

interface MintState {
  readonly independentField: Field
  readonly typedValue: string
  readonly startPriceTypedValue: string

  readonly leftRangeTick: number | undefined
  readonly rightRangeTick: number | undefined
  readonly isFullRange: boolean

  readonly leftRangeTypedValue: string
  readonly rightRangeTypedValue: string

  readonly selectedPreset: PresetRange
}

const initialState: MintState = {
  independentField: Field.CURRENCY_A,
  typedValue: '',
  startPriceTypedValue: '',
  leftRangeTick: undefined,
  rightRangeTick: undefined,
  isFullRange: false,
  leftRangeTypedValue: '',
  rightRangeTypedValue: '',
  selectedPreset: PresetRange.FULL,
}

export default createReducer<MintState>(initialState, (builder) =>
  builder
    .addCase(resetMintState, () => initialState)
    .addCase(setFullRange, (state) => {
      return {
        ...state,
        selectedPreset: PresetRange.FULL,
        leftRangeTick: undefined,
        rightRangeTick: undefined,
        isFullRange: true,
        leftRangeTypedValue: '',
        rightRangeTypedValue: '',
      }
    })
    .addCase(setPresetRange, (state, { payload: { preset } }) => {
      return {
        ...state,
        selectedPreset: preset,
        isFullRange: preset === PresetRange.FULL,
        leftRangeTypedValue: '',
        rightRangeTypedValue: '',
        leftRangeTick: preset === PresetRange.FULL ? undefined : state.leftRangeTick,
        rightRangeTick: preset === PresetRange.FULL ? undefined : state.rightRangeTick,
      }
    })
    .addCase(setPresetTicks, (state, { payload: { preset, leftTick, rightTick } }) => {
      return {
        ...state,
        leftRangeTick: leftTick,
        rightRangeTick: rightTick,
        selectedPreset: preset,
        isFullRange: preset === PresetRange.FULL,
        leftRangeTypedValue: '',
        rightRangeTypedValue: '',
      }
    })
    .addCase(typeStartPriceInput, (state, { payload: { typedValue } }) => {
      return {
        ...state,
        startPriceTypedValue: typedValue,
      }
    })
    .addCase(typeLeftRangeTick, (state, { payload: { tick } }) => {
      const shouldExitFullRange = tick !== undefined && state.isFullRange

      return {
        ...state,
        leftRangeTick: tick,
        isFullRange: shouldExitFullRange ? false : state.isFullRange,
        selectedPreset: PresetRange.CUSTOM,
      }
    })
    .addCase(typeRightRangeTick, (state, { payload: { tick } }) => {
      const shouldExitFullRange = tick !== undefined && state.isFullRange

      return {
        ...state,
        rightRangeTick: tick,
        isFullRange: shouldExitFullRange ? false : state.isFullRange,
        selectedPreset: PresetRange.CUSTOM,
      }
    })
    .addCase(typeLeftRangeInput, (state, { payload: { typedValue } }) => {
      return {
        ...state,
        leftRangeTypedValue: typedValue,
        isFullRange: false,
        selectedPreset: PresetRange.CUSTOM,
      }
    })
    .addCase(typeRightRangeInput, (state, { payload: { typedValue } }) => {
      return {
        ...state,
        rightRangeTypedValue: typedValue,
        isFullRange: false,
        selectedPreset: PresetRange.CUSTOM,
      }
    })
    .addCase(typeInput, (state, { payload: { field, typedValue, noLiquidity } }) => {
      if (noLiquidity) {
        // they're typing into the field they've last typed in
        if (field === state.independentField) {
          return {
            ...state,
            independentField: field,
            typedValue,
          }
        }
        // they're typing into a new field, store the other value
        else {
          return {
            ...state,
            independentField: field,
            typedValue,
          }
        }
      } else {
        return {
          ...state,
          independentField: field,
          typedValue,
        }
      }
    })
)
